import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
  Spinner,
  Text,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type PrFiUserMapping = {
  _id?: string;
  plantCode: string;
  fiCode: string;
  created_At?: string;
  updated_At?: string;
};

const initialForm: PrFiUserMapping = {
  plantCode: "",
  fiCode: "",
};

export default function PrFiUserScreen(): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [form, setForm] = useState<PrFiUserMapping>(initialForm);
  const [list, setList] = useState<PrFiUserMapping[]>([]);

  const [searchPlantCode, setSearchPlantCode] = useState("");
  const [debouncedPlantCode, setDebouncedPlantCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const [deleteTargetId, setDeleteTargetId] = useState<string | undefined>(undefined);

  const handleChange = (name: keyof PrFiUserMapping, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const res: any = await appRequest("prFiUserWithPlantCode", "getAll", {});

      if (res?.status === "success") {
        setList(res?.data || []);
      }
    } catch (err: any) {
      toastNotify({
        title: "Fetch failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPlantCode(searchPlantCode);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchPlantCode]);

  useEffect(() => {
    fetchMappings();
  }, []);

  const handleAdd = async () => {
    if (!form.plantCode || !form.fiCode) {
      toastNotify({
        title: "Validation error",
        message: "Plant Code and FI Code are required",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        await appRequest("prFiUserWithPlantCode", "update", { id: editingId, ...form });
      } else {
        await appRequest("prFiUserWithPlantCode", "create", form);
      }

      toastNotify({
        title: "Success",
        message: editingId ? "Updated successfully" : "Saved successfully",
        type: "success",
      });

      setForm(initialForm);
      setEditingId(undefined);
      fetchMappings();
      onClose();
    } catch (err: any) {
      let errorMessage = err?.message || "Something went wrong";
      
      // Handle MongoDB Duplicate Key Error (E11000)
      if (errorMessage.includes("E11000") || errorMessage.includes("duplicate key")) {
        errorMessage = `The Plant Code "${form.plantCode}" already exists. Please use a unique code.`;
      }

      toastNotify({
        title: "Operation failed",
        message: errorMessage,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setLoading(true);
      await appRequest("prFiUserWithPlantCode", "delete", { id: deleteTargetId });

      toastNotify({
        title: "Deleted",
        message: "Mapping removed successfully",
        type: "success",
      });

      fetchMappings();
      onDeleteClose();
    } catch (err: any) {
      toastNotify({
        title: "Delete failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event: any) => {
      try {
        setLoading(true);
        const base64Content = event.target.result;
        const res: any = await appRequest("prFiUserWithPlantCode", "bulkUpload", { excelData: base64Content });

        if (res?.status === "success") {
          toastNotify({
            title: "Bulk Upload Success",
            message: res?.message || "Data uploaded successfully",
            type: "success",
          });
          fetchMappings();
        }
      } catch (err: any) {
        toastNotify({
          title: "Upload failed",
          message: err?.message || "Something went wrong",
          type: "error",
        });
      } finally {
        setLoading(false);
        e.target.value = ""; // Reset input
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSyncWorkflows = async () => {
    try {
      setLoading(true);
      const res: any = await appRequest("prFiUserWithPlantCode", "syncFiUsers", {});
      
      if (res?.status === "success") {
        toastNotify({
          title: "Sync Successful",
          message: res?.message || "Successfully synced FI users to workflows",
          type: "success",
        });
      }
    } catch (err: any) {
      toastNotify({
        title: "Sync Failed",
        message: err?.message || "Something went wrong while syncing workflows",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredList = list.filter(item => 
    item.plantCode.toLowerCase().includes(debouncedPlantCode.toLowerCase())
  );

  return (
    <ShadowBox>
      <Flex mb={4} align="center" justify="space-between">
        <Box>
          <Text fontWeight="bold" fontSize="2xl" bgGradient="linear(to-r, green.500, teal.400)" bgClip="text">
            PR FI User Master
          </Text>
          <Text fontSize="sm" color="gray.500">Manage mapping between Plant Codes and FI Codes for PR process</Text>
        </Box>

        <HStack spacing={2}>
          <input
            type="file"
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            id="excel-upload-input"
            onChange={handleExcelUpload}
          />
          <Button
            as="label"
            htmlFor="excel-upload-input"
            leftIcon={<span>📁</span>}
            colorScheme="green"
            variant="outline"
            borderRadius="full"
            cursor="pointer"
            _hover={{ bg: "green.50" }}
            isLoading={loading}
          >
            Upload Excel
          </Button>
          <Button
            leftIcon={<span>🔄</span>}
            colorScheme="blue"
            variant="solid"
            borderRadius="full"
            px={6}
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            onClick={handleSyncWorkflows}
            isLoading={loading}
          >
            Sync Workflows
          </Button>
          <Button
            leftIcon={<span>+</span>}
            colorScheme="teal"
            variant="solid"
            borderRadius="full"
            px={8}
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            onClick={() => {
              setForm(initialForm);
              setEditingId(undefined);
              setIsViewOnly(false);
              onOpen();
            }}
          >
            Add Mapping
          </Button>
        </HStack>
      </Flex>

      <HStack spacing={4} mb={6} bg="gray.50" p={4} borderRadius="xl">
        <CustomInput
          name="searchPlantCode"
          label="Search Plant Code"
          value={searchPlantCode}
          onChange={(e: any) => setSearchPlantCode(e.target.value)}
        />
      </HStack>

      <Box mt={3} maxH="60vh" overflowY="auto" borderRadius="xl" border="1px" borderColor="gray.100">
        {loading && list.length === 0 ? (
          <Flex justify="center" p={10}>
            <Spinner color="teal.500" size="xl" thickness="4px" />
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th py={4}>Plant Code</Th>
                <Th py={4}>FI Code</Th>
                <Th py={4}>Created Date</Th>
                <Th py={4} textAlign="center">Action</Th>
              </Tr>
            </Thead>

            <Tbody>
              {filteredList.map((item) => (
                <Tr key={item._id} _hover={{ bg: "teal.50" }} transition="0.2s">
                  <Td fontWeight="600">{item.plantCode}</Td>
                  <Td>
                    <Badge colorScheme="teal" variant="subtle" borderRadius="full" px={2}>
                      {item.fiCode}
                    </Badge>
                  </Td>
                  <Td fontSize="xs" color="gray.600">
                    {item.created_At ? new Date(item.created_At).toLocaleDateString() : "N/A"}
                  </Td>
                  <Td>
                    <HStack justify="center" spacing={2}>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="teal"
                        borderRadius="full"
                        onClick={() => {
                          setForm({ ...item });
                          setEditingId(item._id);
                          setIsViewOnly(true);
                          onOpen();
                        }}
                      >
                        View
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="teal"
                        borderRadius="full"
                        onClick={() => {
                          setForm({ ...item });
                          setEditingId(item._id);
                          setIsViewOnly(false);
                          onOpen();
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        onClick={() => {
                          setDeleteTargetId(item._id);
                          onDeleteOpen();
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {filteredList.length === 0 && !loading && (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={10}>
                    <Text color="gray.500">No mappings found</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* DRAWER FOR ADD/EDIT */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="right">
        <DrawerOverlay backdropFilter="blur(15px)" bg="blackAlpha.700" />
        <DrawerContent maxW="50%" borderRadius="30px 0 0 30px" boxShadow="-20px 0 50px rgba(0,0,0,0.2)">
          <DrawerCloseButton
            size="lg"
            top="4"
            right="6"
            borderRadius="full"
            bg="whiteAlpha.800"
            _hover={{ bg: "red.50", color: "red.500" }}
          />
          <DrawerHeader
            borderBottomWidth="1px"
            bgGradient="linear(to-r, teal.600, green.400)"
            color="white"
            borderTopLeftRadius="30px"
            py={5}
            px={8}
          >
            <Box>
              <Text fontSize="2xl" fontWeight="900" letterSpacing="tight">
                {isViewOnly ? "MAPPING VIEW" : (editingId ? "REFINE MAPPING" : "CREATE NEW MAPPING")}
              </Text>
              <Text fontSize="xs" fontWeight="bold" opacity="0.9" textTransform="uppercase">
                Plant Code to FI User Mapping
              </Text>
            </Box>
          </DrawerHeader>

          <DrawerBody py={6} px={6} bg="gray.50">
            <VStack spacing={6} align="stretch">
              <Box bg="white" p={5} borderRadius="2xl" boxShadow="sm" border="1px" borderColor="gray.100">
                <SimpleGrid columns={1} spacing={6}>
                  <CustomInput
                    name="plantCode"
                    label="Plant Code"
                    placeholder="e.g. 1101"
                    value={form.plantCode}
                    onChange={(e: any) => handleChange("plantCode", e.target.value)}
                    isReadOnly={isViewOnly}
                  />

                  <CustomInput
                    name="fiCode"
                    label="FI Code (User ID)"
                    placeholder="e.g. FIUSER01"
                    value={form.fiCode}
                    onChange={(e: any) => handleChange("fiCode", e.target.value)}
                    isReadOnly={isViewOnly}
                  />
                </SimpleGrid>
              </Box>
            </VStack>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" gap={3} py={4} px={8} bg="white">
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="full" px={8} size="sm">
              {isViewOnly ? "EXIT VIEW" : "CANCEL"}
            </Button>
            {!isViewOnly && (
              <Button 
                colorScheme="teal" 
                onClick={handleAdd} 
                isLoading={loading}
                borderRadius="full"
                px={12}
                h="45px"
                fontSize="sm"
                fontWeight="900"
                boxShadow="0 10px 20px -5px rgba(49, 151, 149, 0.4)"
              >
                {editingId ? "UPDATE MAPPING" : "SAVE MAPPING"}
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* DELETE CONFIRM MODAL */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(8px)" />
        <ModalContent borderRadius="3xl" p={2}>
          <ModalHeader fontSize="xl" fontWeight="800" textAlign="center" pt={6}>
            ⚠️ Confirm Deletion
          </ModalHeader>
          <ModalBody py={6} textAlign="center">
            <Text fontSize="md" color="gray.600">
              Are you sure you want to remove the mapping for plant <b>{list.find(i => i._id === deleteTargetId)?.plantCode}</b>?
            </Text>
            <Text fontSize="xs" color="red.400" mt={2} fontWeight="bold">
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter gap={3} pb={6} justifyContent="center">
            <Button variant="ghost" borderRadius="full" px={8} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              borderRadius="full" 
              px={10} 
              onClick={handleDelete} 
              isLoading={loading}
              boxShadow="0 10px 15px -3px rgba(229, 62, 62, 0.4)"
            >
              Yes, Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ShadowBox>
  );
}
